import * as React from 'react';
import {Doughnut, Pie} from 'react-chartjs-2';
import styled from 'styled-components';
import {transparentize} from 'polished';

export interface DoughnutChartItem {
  label: string;
  value: number;
}

export interface DoughnutChartProps {
  data: DoughnutChartItem[];
  title?: string;
  description?: string;
  colors?: string[];
  pie?: boolean; // true if need pie chart
}

const Container = styled.div`
  position: relative;
  width: max-content;
`;

export const Content = styled.div`
  width: 236px;
  height: 236px;
`;

export const Info = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  pointer-events: none;
`;

export const Title = styled.div`
  width: max-content;
  margin-bottom: 4px;
  font-size: 22px;
  font-weight: 500;
  line-height: 31px;
  letter-spacing: 0.01em;
  max-width: 92px;
  text-align: center;
`;

export const Description = styled.div`
  width: max-content;
  max-width: 92px;
  font-size: 14px;
  text-align: center;
  color: ${({theme}) => `
    ${transparentize(.6, theme.variables.color)};
  `};
`;

/**
 * The component render doughnut chart
 * @param {DoughnutChartProps} props
 * @return {React.ReactElement}
 * @constructor
 */
export default function DoughnutChart(props: DoughnutChartProps) {
  const ref = React.useRef();

  const defaultColors = [
    '#FFBB56',
    '#FFD872',
    '#FF535C',
    '#FF8456',
    '#FF9172',
    '#FFAFAF',
    '#FFF8E9',
    '#BCF4DE',
    '#8CFFDA',
    '#6BD18E',
    '#EFFFAF',
    '#AFF5FF',
    '#C8B6FF',
    '#8649FF',
    '#AA55F7',
    '#F13F9F',
    '#FF6DAE',
    '#FFA6D4',
  ];

  /**
   * The return prepare and return data for doughnut chart
   * @return {Doughnut}
   */
  function prepareData() {
    const labels :string[] = [];
    const values :number[] = [];

    props.data.forEach((item) => {
      labels.push(item.label);
      values.push(item.value);
    });

    return {
      labels,
      datasets: [{
        data: values,
        backgroundColor: props.colors || defaultColors,
        hoverOffset: 10,
      }],
    };
  }

  /**
   * render doughnut chart info
   * @return {React.ReactElement | null}
   */
  function renderInfo(): JSX.Element | null {
    const {title, description} = props;

    const titleComponent = title
      ? <Title>{title}</Title>
      : null;

    const descriptionComponent = description
      ? <Description>{description}</Description>
      : null;

    if (title || description) {
      return (
        <Info>
          {titleComponent}
          {descriptionComponent}
        </Info>
      );
    }

    return null;
  }

  function getChartOptions() {
    const defaultOptions = {
      maintainAspectRatio: true,
      plugins: {
        legend: false,
        labels: false,
        tooltip: {
          enable: true,
          external: ()=>{},
        },
      },

      borderWidth: 0,
      cutout: 75,
      radius: 110,
    }

    if (props.pie) {
      return {
        ...defaultOptions,
        cutout: 0,
      }
    }

    return defaultOptions
  }

  /**
   * render doughnut chart content
   * @return {React.ReactElement | null}
   */
  function renderContent(): JSX.Element | null {
    return (
      <Content>
        <Pie
          ref={ref}
          data={prepareData()}
          width={236}
          height={236}
          options={getChartOptions()}
        />
      </Content>
    );
  }

  return (
    <Container>
      {renderInfo()}
      {renderContent()}
    </Container>
  );
}
